import React, { useEffect, useState } from 'react';
import { X, Download, Mouse, Keyboard, Film, LogIn, ListChecks, Play } from 'lucide-react';

/**
 * OnboardingModal — shown ONCE per browser on the presenter's very first
 * dashboard visit. Explains: how to log in, how to configure the deck,
 * and how to navigate during the live presentation.
 *
 * Persistence: localStorage flag `stig_onboarding_seen`. Once dismissed
 * (button click or PDF download), it never auto-opens again on this
 * device. Presenters can re-open via the "?" button in the dashboard
 * header if we add one later — out of scope for this cut.
 */
const STORAGE_KEY = 'stig_onboarding_seen';

export default function OnboardingModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch (_) { /* ignore */ }
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="onboarding-backdrop" data-testid="onboarding-modal">
      <div className="onboarding-card" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
        <button
          type="button"
          className="onboarding-close"
          onClick={dismiss}
          aria-label="Willkommens-Fenster schließen"
          data-testid="onboarding-close"
        >
          <X size={16} strokeWidth={1.6} />
        </button>

        <div className="onboarding-inner">
          <div className="onboarding-kicker">STIG ROCK · WILLKOMMEN</div>
          <h2 id="onboarding-title" className="onboarding-title font-display">
            Kurze Einführung
          </h2>
          <p className="onboarding-sub">
            Drei einfache Schritte, damit du sofort loslegen kannst.
          </p>

          <ol className="onboarding-steps">
            <li>
              <div className="onboarding-step-icon"><LogIn size={18} strokeWidth={1.5} /></div>
              <div>
                <div className="onboarding-step-title">1 · Eingeloggt — perfekt</div>
                <p>
                  Deine Sitzung bleibt für dieses Gerät aktiv. Du musst
                  dich nicht bei jedem Öffnen neu anmelden.
                </p>
              </div>
            </li>
            <li>
              <div className="onboarding-step-icon"><ListChecks size={18} strokeWidth={1.5} /></div>
              <div>
                <div className="onboarding-step-title">2 · Präsentation einstellen</div>
                <p>
                  Trage deine <strong>Agenda</strong> ein (je Zeile ein
                  Punkt) und schalte im Bereich darunter jene <strong>
                  Folien</strong> aus, die du nicht zeigen willst.
                  Änderungen werden automatisch gespeichert.
                </p>
              </div>
            </li>
            <li>
              <div className="onboarding-step-icon"><Play size={18} strokeWidth={1.5} /></div>
              <div>
                <div className="onboarding-step-title">3 · Präsentation starten</div>
                <p>
                  Klick oben auf <em>&bdquo;Präsentation starten&ldquo;</em>. Du landest
                  direkt auf Folie 1.
                </p>
              </div>
            </li>
          </ol>

          <div className="onboarding-divider" />

          <div className="onboarding-kicker">Navigation in der Präsentation</div>

          <div className="onboarding-nav-grid">
            <div>
              <div className="onboarding-nav-head">
                <Mouse size={14} strokeWidth={1.5} />
                <span>Maus</span>
              </div>
              <ul>
                <li><kbd>Linksklick</kbd> — nächste Folie</li>
                <li><kbd>Rechtsklick</kbd> — vorherige Folie</li>
              </ul>
            </div>
            <div>
              <div className="onboarding-nav-head">
                <Keyboard size={14} strokeWidth={1.5} />
                <span>Tastatur / Klicker</span>
              </div>
              <ul>
                <li><kbd>Leertaste</kbd> / <kbd>↓</kbd> / <kbd>→</kbd> — vor</li>
                <li><kbd>↑</kbd> / <kbd>←</kbd> — zurück</li>
              </ul>
            </div>
            <div>
              <div className="onboarding-nav-head">
                <Film size={14} strokeWidth={1.5} />
                <span>Kino-Modus</span>
              </div>
              <ul>
                <li>Button <strong>oben rechts</strong> in der Präsentation</li>
                <li>Blendet Menüs & Cursor für einen sauberen Look aus</li>
              </ul>
            </div>
          </div>

          <div className="onboarding-actions">
            {/* Temporarily disabled — presenter guide PDF is being regenerated
                to match the new SlideDeck layout. Re-enable once the guide
                reflects the current design.
            <a
              href="/stig-rock-presenter-guide.pdf"
              download
              className="onboarding-btn onboarding-btn-primary"
              data-testid="onboarding-download-pdf"
              onClick={() => {
                // Mark as seen when they explicitly download — most
                // presenters treat this as "I got what I need".
                try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { }
              }}
            >
              <Download size={14} strokeWidth={1.6} />
              <span>Als PDF herunterladen</span>
            </a>
            */}
            <button
              type="button"
              onClick={dismiss}
              className="onboarding-btn onboarding-btn-primary"
              data-testid="onboarding-dismiss"
            >
              Verstanden, los geht&apos;s
            </button>
          </div>

          <div className="onboarding-fineprint">
            Bei Fragen wende dich an das <strong>Head Office</strong>.
          </div>
        </div>
      </div>
    </div>
  );
}
